import { AlertState, ConfirmState } from "@/common/alertConfirm";
import { StringFieldModel } from "@/common/commonForms";
import { ModuleName } from "@/common/product";
import { ConversionProps } from "@/components-v2/FilePreview";
import { ProjectController } from "@/dbot/project/projectDomain";
import { DialogType } from "@/dialogs/dialogDomain";
import { FileUploadConfig, FileUploadValidationError } from "@/fileUpload/fileUploadDomain";
import { Language } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";
import { MeetingType } from "@/meetings/meetingsDomain";
import { SelectedPortfolioOption } from "@/portfolio/portfolioDomain";
import { createHref, RouteWithParams } from "@/routes/router";
import { DCRoute, DCRouteData, routes } from "@/routes/routes";
import AlertMessageStore, {
  AlertMessageParams,
  AlertMessageText,
  AlertMessageType,
} from "@/stores/AlertMessageStore";
import { OpenSendInvitationDialogParams } from "@/stores/SendInvitationDialogStore";
import { CompanyInfo } from "@/types/CompanyInfo";
import { ObjectPermission } from "@/types/Permissions";
import { Transaction } from "@/types/Transactions";
import { User } from "@/types/UserInfo";
import { isDebugLoggingEnabled, logDebug, logError } from "@/utils/log";
import { openLinkInNewWindow } from "@/utils/navigation";
import {
  SubscribeToWebSocketMessagesEvent,
  UnsubscribeFromWebSocketMessagesEvent,
} from "@/webSocketDomain";
import { RecordTelemetryEvent } from "@/telemetry/telemetryDomain";
import { AxiosInstance } from "axios";
import { History } from "history";
import { action, isObservable, toJS } from "mobx";
import { RootStoreData } from "./rootStoreData";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type HttpResponse<T = any> = {
  data: T;
  headers?: { [key: string]: string | string[] | number | boolean | null };
};

/**
 * Using interface instead of type here because the SignedDocumentEvent
 * union type is recursive. It uses the HttpEvent with itself as the
 * generic type.
 *
 * Types are evaluated/expanded eagerly by TypeScript, whereas interfaces
 * are evaluated lazily, meaning that TypeScript can only handle recursive
 * types with interfaces.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface HttpEvent<T = any> extends HttpEventCallbacks<T> {
  type: "http";
  method: "get" | "post" | "put" | "delete";
  url: string;
  headers?: { [key: string]: string };
  responseType?: "arraybuffer" | "blob" | "document" | "json" | "text" | "stream" | "formdata";
  data?: unknown;
}

export interface HttpEventCallbacks<T> {
  onSuccess: (res: HttpResponse<T>, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
}

export type LoadUrlEvent = { type: "load-url"; url: string; newWindow?: boolean };

export type NavigateEvent<S = unknown> = {
  type: "navigate";
  url: string;
  replace?: boolean;
  state?: S;
  newWindow?: boolean;
};

export type NavigateToEvent<R extends DCRoute = DCRoute> = {
  type: "navigate-to";
  route: RouteWithParams<DCRouteData, R>;
  replace?: boolean;
  state?: unknown;
};

export const navigateTo = <R extends DCRoute>(
  route: RouteWithParams<DCRouteData, R>,
  options?: Pick<NavigateToEvent, "replace" | "state">,
): NavigateToEvent<R> => ({ type: "navigate-to", route, ...options });

export type ReloadEvent = { type: "reload" };

export type ShowAlertEvent = {
  type: "show-alert";
  message: AlertMessageText;
  messageType: AlertMessageType;
  messageParams?: AlertMessageParams;
};

export type UpdateStoreEvent<T = RootStoreData> = {
  type: "update-store";
  data: {
    [K in keyof T]?: T[K] | ((cur: T[K]) => T[K]);
  };
};

export const updateStore = (data: {
  [K in keyof RootStoreData]?: RootStoreData[K] | ((cur: RootStoreData[K]) => RootStoreData[K]);
}): UpdateStoreEvent => ({ type: "update-store", data });

export interface WithStoreEvent<T = RootStoreData> {
  type: "with-store";
  events: (store: T) => DCEvent[];
}

export type LogErrorEvent = { type: "log-error"; err?: unknown; msg: string; args?: unknown[] };

export type ActionNameEvent = { type: "action-name"; name: string; args?: unknown[] };

/**
 * Used to describe a list of events for debugging purposes
 * @param name
 */
export const actionName = (name: string, ...args: unknown[]): ActionNameEvent => ({
  type: "action-name",
  name,
  args,
});

export function logErrorEvent(msg: string, ...args: unknown[]): LogErrorEvent;
export function logErrorEvent(err: unknown, msg: string, ...args: unknown[]): LogErrorEvent;
export function logErrorEvent(
  errOrMsg: unknown,
  msgOrArg: unknown,
  ...args: unknown[]
): LogErrorEvent {
  if (typeof errOrMsg === "string") {
    return {
      type: "log-error",
      msg: errOrMsg,
      args: msgOrArg !== undefined || args.length ? [msgOrArg, ...args] : args,
    };
  }
  return { type: "log-error", err: errOrMsg, msg: msgOrArg as string, args };
}

export const handleLogErrorEvent = (event: LogErrorEvent, context: unknown) => {
  if (event.err) {
    logError(event.err, event.msg, { args: event.args, context });
  } else {
    logError(event.msg, { args: event.args, context });
  }
};

export const handleHttpEvent = (
  http: AxiosInstance,
  dispatch: (events: DCEvent[]) => void,
  event: HttpEvent,
  state: RootStoreData,
) => {
  http
    .request({
      method: event.method,
      url: event.url,
      headers: event.headers,
      data: event.data,
      responseType: event.responseType,
    })
    .then((res) => dispatch(event.onSuccess(res as HttpResponse, state)))
    .catch((err) => dispatch(event.onFailure(err, state)));
};

export const handleLoadUrlEvent = (event: LoadUrlEvent) => {
  if (event.newWindow) {
    openLinkInNewWindow(event.url);
  } else {
    window.location.href = event.url;
  }
};

export const handleReloadEvent = () => {
  window.location.reload();
};

export const handleShowAlertEvent = (alertStore: AlertMessageStore, event: ShowAlertEvent) => {
  alertStore.showMessage(event.messageType, event.message, event.messageParams);
};

export const handleCopyToClipboardEvent = (
  event: CopyToClipboardEvent,
  dispatch: EventDispatcher,
) => {
  const { value } = event;
  (typeof value === "string"
    ? navigator.clipboard.writeText(value)
    : navigator.clipboard.write([new ClipboardItem({ [value.mimeType]: value.blob })])
  )
    .then(() => dispatch([showSuccess({ key: "general.copy-to-clipboard.success" })]))
    .catch((error) => {
      logError(error, "Failed to copy to clipboard");
      dispatch([showError({ key: "general.copy-to-clipboard.failure" })]);
    });
};

export const handleUpdateStoreEvent = action(
  <T extends { [key: string]: unknown } = RootStoreData>(
    store: T,
    event: UpdateStoreEvent<T>,
  ): T => {
    const preppedData = Object.entries(event.data).map(([k, v]) => [
      k,
      typeof v === "function" ? v(store[k]) : v,
    ]);
    preppedData.forEach(([k, v]) => {
      if (isDebugLoggingEnabled()) {
        logDebug("-", k, isObservable(v) ? toJS(v) : v);
      }
      store[k as keyof T] = v;
    });
    return store;
  },
);

export const handleNavigateEvent = (history: History, event: NavigateEvent) => {
  if (event.replace) {
    history.replace(event.url, event.state);
  } else if (event.newWindow) {
    openLinkInNewWindow(event.url);
  } else {
    history.push(event.url, event.state);
  }
};

export const handleNavigateToEvent = (history: History, event: NavigateToEvent) => {
  const href = createHref<DCRouteData, DCRoute>({ routes: routes, ...event.route });
  if (event.replace) {
    history.replace(href, event.state);
  } else {
    history.push(href, event.state);
  }
};

export const handleThunkEvent = (event: ThunkEvent, dispatch: EventDispatcher) => {
  const result = event.thunk?.(dispatch);
  if (Array.isArray(result)) {
    dispatch(result);
  }
};

export type Timeouts = Map<string, ReturnType<typeof setTimeout>>;

export const handleSetTimeoutEvent = (
  timeouts: Timeouts,
  event: SetTimeoutEvent,
  dispatch: EventDispatcher,
) => {
  const isLeading = !timeouts.has(event.timeoutId);
  clearTimeout(timeouts.get(event.timeoutId));
  timeouts.set(
    event.timeoutId,
    setTimeout(() => {
      timeouts.delete(event.timeoutId);
      dispatch(event.onTimeout);
    }, event.timeoutMs),
  );
  if (isLeading && event.onLeading) {
    dispatch(event.onLeading);
  }
};

export const handleClearTimeoutEvent = (timeouts: Timeouts, event: ClearTimeoutEvent) => {
  clearTimeout(timeouts.get(event.timeoutId));
  timeouts.delete(event.timeoutId);
};

export const httpGet = (params: Omit<HttpEvent, "type" | "method">): HttpEvent => ({
  type: "http",
  method: "get",
  ...params,
});

export const httpPost = (params: Omit<HttpEvent, "type" | "method">): HttpEvent => ({
  type: "http",
  method: "post",
  ...params,
});

export const httpPut = (params: Omit<HttpEvent, "type" | "method">): HttpEvent => ({
  type: "http",
  method: "put",
  ...params,
});

export const httpDelete = (params: Omit<HttpEvent, "type" | "method">): HttpEvent => ({
  type: "http",
  method: "delete",
  ...params,
});

export const navigate = (
  url: string,
  options?: { replace?: boolean; state?: unknown; newWindow?: boolean },
): NavigateEvent => ({
  type: "navigate",
  url,
  ...options,
});

export const loadUrl = (url: string, newWindow: boolean = true): LoadUrlEvent => ({
  type: "load-url",
  url,
  newWindow,
});

export const showAlert = (
  message: AlertMessageText,
  messageType?: AlertMessageType,
  messageParams?: AlertMessageParams,
): ShowAlertEvent => ({
  type: "show-alert",
  messageType: messageType ?? "success",
  message,
  messageParams: {
    ...messageParams,
    autoHide: messageParams?.autoHide ?? (messageType === "success" || messageType === "info"),
  },
});

export const showGeneralError = () => showAlert({ key: "general-error" }, "error");

export const showGeneralSuccess = () =>
  showAlert({ key: "general-success" }, "success", { autoHide: true });

export const showSuccess = (message: AlertMessageText, params?: AlertMessageParams) =>
  showAlert(message, "success", params);

export const showWarning = (message: AlertMessageText, params?: AlertMessageParams) =>
  showAlert(message, "warning", params);

export const showInfo = (message: AlertMessageText, params?: AlertMessageParams) =>
  showAlert(message, "info", params);

export const showError = (message: AlertMessageText, params?: AlertMessageParams) =>
  showAlert(message, "error", params);

export const reload = (): ReloadEvent => ({ type: "reload" });

type ThunkEvent = {
  type: "thunk";
  thunk: ((dispatch: EventDispatcher) => DCEvent[] | unknown) | undefined;
};

export type ClipboardImage = { mimeType: string; blob: Promise<Blob> };

export type CopyToClipboardEvent = {
  type: "copy-to-clipboard";
  value: string | ClipboardImage;
};

export type ValidateFilesEvent = {
  type: "validate-files";
  files: File[];
  fileUploadConfig: FileUploadConfig;
  onSuccess: DCEvent[];
  onFailure: (message: TMessage, error: FileUploadValidationError) => DCEvent[];
};

export type DownloadFileEvent = {
  type: "download-file";
  url: string;
  method?: HttpEvent["method"];
  data?: HttpEvent["data"];
  onSuccess?: DCEvent[];
  onFailure?: DCEvent[];
};

export type UpdateCompanyInfoEvent = {
  type: "update-company-info";
};

export type SetTimeoutEvent = {
  type: "set-timeout";
  timeoutId: string;
  timeoutMs: number;
  onTimeout: DCEvent[];
  onLeading?: DCEvent[];
};

export type ClearTimeoutEvent = {
  type: "clear-timeout";
  timeoutId: string;
};

export type DCEvent =
  | { type: "closeDialog" }
  | {
      type: "editProjectName";
      name: StringFieldModel;
      save: ((name: string) => void) | ((name: string) => DCEvent[]) | DCEvent[];
    }
  | {
      type: "fetchTransactionLog";
      orgNo: string;
      onSuccess: (transactionLog: Transaction[]) => void;
    }
  | {
      type: "load-project";
      projectId: string;
      onSuccess: (ctrl: ProjectController) => DCEvent[];
      onFailure: (err: unknown) => DCEvent[];
    }
  | {
      type: "new-meeting";
      meetingType: MeetingType;
      orgNo: string;
      projectName?: TMessage;
      projectId?: string;
    }
  | {
      type: "preview-file";
      filename?: string;
      file: { url: string } | { data: ArrayBuffer | Blob };
      onClose?: () => void;
      conversion?: ConversionProps;
    }
  | { type: "showAcceptCompanyTermsDialog"; companyInfo: CompanyInfo; user: User }
  | { type: "showCreatePortfolioDialog" }
  | {
      type: "showEditEmailDialog";
      externalId: string;
      email: string;
      onSuccess: (email: string) => void;
      name: string;
      customRoleId: string;
    }
  | { type: "showGeneralMeetingAnnouncementDialog" }
  | { type: "showInviteNewAdminDialog"; lang: Language; portfolio: SelectedPortfolioOption }
  | { type: "showPaymentDialog" }
  | {
      type: "showRevokeAllDialog";
      orgNos: string[];
      externalId: string;
      name: string;
      onSuccess: () => void;
      portfolio: SelectedPortfolioOption;
    }
  | {
      type: "showSendInvitationDialog";
      data: OpenSendInvitationDialogParams;
    }
  | { type: "showVerifyEmailDialog" }
  | { type: "update-local-storage"; key: string; val: unknown }
  | { type: "update-session-storage"; key: string; val: string }
  | { type: "show-dialog"; dialog: DialogType }
  | {
      type: "showChangePermissionDialog";
      title: string | TMessage;
      objectId: string;
      moduleName: ModuleName;
      onUpdate: (permissions: ObjectPermission[]) => void;
    }
  | ActionNameEvent
  | ClearTimeoutEvent
  | CopyToClipboardEvent
  | DownloadFileEvent
  | HttpEvent
  | LoadUrlEvent
  | LogErrorEvent
  | NavigateEvent
  | NavigateToEvent
  | OrderLiveGeneralMeetingEvent
  | RecordTelemetryEvent
  | ReloadEvent
  | SetTimeoutEvent
  | ShowAlertEvent
  | SubscribeToWebSocketMessagesEvent
  | ThunkEvent
  | UnsubscribeFromWebSocketMessagesEvent
  | UpdateCompanyInfoEvent
  | UpdateStoreEvent
  | ValidateFilesEvent
  | WithStoreEvent;

export type OrderLiveGeneralMeetingEvent = {
  type: "order-live-general-meeting";
  orgNo: string;
  projectId?: string;
  generalMeetingDate: string;
  onSuccess: () => void;
  onFailure: () => void;
};

export type EventDispatcher = (events: DCEvent[]) => void;

export const createEventDispatcher =
  (handler: (event: DCEvent, index: number, events: DCEvent[]) => void): EventDispatcher =>
  (events: DCEvent[]) => {
    if (!events.length) {
      return;
    }
    if (events.length === 1) {
      logDebug("dispatch", events[0].type, events[0]);
      handler(events[0], 0, events);
    } else {
      const nameEvent = events[0].type === "action-name" ? events[0] : undefined;
      logDebug(
        "dispatch",
        nameEvent?.name ?? events.map((e) => e.type),
        ...(nameEvent?.args ?? []),
      );
      events.forEach((e, i, es) => {
        if (i !== 0 || !nameEvent) {
          logDebug(e.type, e);
        }
        handler(e, i, es);
      });
    }
  };

export type Loading = { type: "loading" };

export const loading: Loading = { type: "loading" };
export const showPaymentDialog: DCEvent = { type: "showPaymentDialog" };
export const showCreatePortfolioDialog: DCEvent = { type: "showCreatePortfolioDialog" };
export const showVerifyEmailDialog: DCEvent = { type: "showVerifyEmailDialog" };

export const confirmAction = (params: ConfirmState): DCEvent[] => [
  {
    type: "show-dialog",
    dialog: {
      type: "confirm",
      state: {
        ...params,
        onConfirm: [{ type: "closeDialog" }, ...params.onConfirm],
        onCancel: [{ type: "closeDialog" }, ...(params.onCancel ?? [])],
      },
    },
  },
];

export const alertAction = ({ title, text, image, onClose }: AlertState): DCEvent[] => [
  {
    type: "show-dialog",
    dialog: {
      type: "alert",
      state: {
        title,
        text,
        image,
        onClose: [{ type: "closeDialog" }, ...(onClose ?? [])],
      },
    },
  },
];

export const thunk = (thunk: ThunkEvent["thunk"]): ThunkEvent => ({
  type: "thunk",
  thunk,
});

export const copyToClipboard = (value: string | ClipboardImage): CopyToClipboardEvent => ({
  type: "copy-to-clipboard",
  value,
});

export const debounceEvents = (
  timeoutId: string,
  timeoutMs: number,
  events: DCEvent[],
  { onLeading }: { onLeading?: DCEvent[] } = {},
): SetTimeoutEvent => ({ type: "set-timeout", timeoutId, timeoutMs, onTimeout: events, onLeading });
